<template>
  <b-card>
    <div class="file-upload-container">
      <!-- Progress Bar Overlay -->
      <div
        v-if="uploading"
        class="progress-overlay border-light"
      >
        <b-spinner
          type="grow"
          variant="primary"
        />
        <upload-progress
          :progress="uploadProgress"
          class="mt-2 px-2"
          style="max-width: 400px"
        />
      </div>

      <!-- Dosya Yükleme Alanı -->
      <b-card
        class="file-drop-zone"
        :class="{
          'border-primary bg-light': isDragOver,
          'has-file': selectedFile,
        }"
        no-body
        @drop="handleDrop"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @click="triggerFileInput"
      >
        <!-- Dosya Seçimi Input -->
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg, image/png, image/gif, image/bmp, image/webp,video/mp4,video/webp,video/webm"
          class="d-none"
          :disabled="!!dataItem.id"
          @change="handleFileSelect"
        >

        <!-- Boş Durum -->
        <div
          v-if="!dataItem.file_name"
          class="upload-placeholder text-center py-5"
        >
          <div class="upload-icon mb-2">
            <feather-icon
              icon="UploadCloudIcon"
              size="50"
            />
          </div>
          <h4 class="upload-title text-dark mb-2">
            Dosya Seç
          </h4>
          <p class="upload-description text-muted mb-2">
            Dosyayı buraya sürükleyip bırakın veya seçmek için tıklayın
          </p>
        </div>

        <!-- Seçilen Dosya -->
        <div
          v-else
          class="selected-file p-4"
        >
          <div class="file-info d-flex align-items-center">
            <div class="file-icon mr-3">
              <feather-icon
                :icon="getFileIcon(dataItem.file_type)"
                size="40"
              />
            </div>
            <div class="file-details flex-grow-1">
              <h5 class="file-name mb-1">
                {{ dataItem.file_name }}
              </h5>
              <div class="file-meta d-flex gap-3">
                <small class="text-muted mr-1">{{ formatFileSize(dataItem.file_size) }}</small>
                <small class="text-muted">{{ dataItem.file_type || 'Bilinmeyen tür' }}</small>

              </div>
            </div>
            <div class="file-actions">
              <b-button
                v-if="!uploading && !dataItem.id"
                variant="outline-danger"
                size="sm"
                @click.stop="removeFile"
              >
                <feather-icon icon="XIcon" />
                Kaldır
              </b-button>
            </div>
          </div>
        </div>
      </b-card>
    </div>
    <b-row>
      <b-col
        cols="12"
        md="6"
        offset-md="3"
      >
        <b-form-group
          label="Medya Başlık *"
          label-for="title"
        >
          <template #label>
            Medya Başlık <span class="text-danger">*</span>
          </template>
          <b-form-input
            id="title"
            v-model="dataItem.title"
            placeholder="Medya Başlık"
          />
          <span class="font-small-2 text-muted"><span class="text-danger">*</span> İsteğe bağlı başlık, açıklama</span>
        </b-form-group>
      </b-col>
      <b-col cols="12">
        <div class="text-center">
          <b-button
            variant="primary"
            :disabled="uploading"
            @click="submitForm"
          >
            <FeatherIcon icon="UploadIcon" />
            Dosya Yükle
          </b-button>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BButton,
  BSpinner,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
} from 'bootstrap-vue'
import UploadProgress from '@/layouts/components/common/UploadProgress.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'MediaUploadCard',
  components: {
    UploadProgress,
    BCard,
    BButton,
    BSpinner,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
  },
  props: {
    getDataList: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      selectedFile: null,
      isDragOver: false,
      baseUrl: this.$store.state.app.baseURL,
      userData: JSON.parse(localStorage.getItem('userData')),
      progressInterval: null,
    }
  },
  computed: {
    uploading() {
      return this.$store.getters['productMediaGallery/uploading']
    },
    uploadProgress() {
      return this.$store.getters['productMediaGallery/uploadProgress']
    },
    dataItem() {
      return this.$store.getters['productMediaGallery/dataItem']
    },
    saveData() {
      return this.$store.getters['productMediaGallery/dataSaveStatus']
    },
  },
  watch: {
    saveData(val) {
      if (val.status === true) {
        this.showToast({
          variant: 'success', title: 'İşlem Başarılı!', icon: 'CheckIcon', text: val.message,
        })
      } else if (val.status === false) {
        this.showToast({
          variant: 'danger', title: 'İşlem Hatası!', icon: 'XIcon', text: val.message,
        })
      }
    },
  },
  beforeDestroy() {
    // Memory leak'i önlemek için interval'ı temizle
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
  },
  created() {
    this.$store.commit('productMediaGallery/RESET_DATA_ITEM')
  },
  methods: {
    // Dosya seçimi tetikleme
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    // Dosya seçimi işleme
    handleFileSelect(event) {
      const files = Array.from(event.target.files)
      if (files.length > 0) {
        this.dataItem.media_type = null
        // eslint-disable-next-line prefer-destructuring
        this.selectedFile = files[0]
        this.dataItem.file_name = this.selectedFile.name
        this.dataItem.file_size = this.selectedFile.size
        this.dataItem.file_type = this.selectedFile.type
        this.dataItem.upload_file = this.selectedFile
        if (this.selectedFile.type.startsWith('image/')) {
          this.dataItem.media_type = 'image'
        }
        if (this.selectedFile.type.startsWith('video/')) {
          this.dataItem.media_type = 'video'
        }
        if (this.fileChange) this.fileChange = false
      }
      // Input'u temizle
      // eslint-disable-next-line no-param-reassign
      event.target.value = ''
    },

    // Sürükleme olayları
    handleDragOver(event) {
      event.preventDefault()
      if (this.dataItem.id && !this.fileChange) return
      this.isDragOver = true
    },

    handleDragLeave(event) {
      event.preventDefault()
      if (this.dataItem.id && !this.fileChange) return
      this.isDragOver = false
    },

    handleDrop(event) {
      event.preventDefault()
      this.isDragOver = false
      if (this.dataItem.id && !this.fileChange) return

      const files = Array.from(event.dataTransfer.files)
      if (files.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.selectedFile = files[0]
        this.dataItem.file_name = this.selectedFile.name
        this.dataItem.file_size = this.selectedFile.size
        this.dataItem.file_type = this.selectedFile.type
        this.dataItem.upload_file = this.selectedFile
        if (this.fileChange) this.fileChange = false
      }
    },

    // Dosya kaldırma
    removeFile() {
      this.selectedFile = null
      this.dataItem.file_name = null
    },

    // Dosya boyutunu formatlama
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'

      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))

      // eslint-disable-next-line no-restricted-properties
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
    },

    // Dosya türüne göre ikon belirleme
    getFileIcon(type) {
      if (!type) return 'fFileTextIcon'

      if (type.startsWith('image/')) return 'ImageIcon'
      if (type.startsWith('video/')) return 'VideoIcon'

      return 'FileTextIcon'
    },

    submitForm() {
      this.dataItem.item_id = this.$route.params.id
      this.$store.dispatch('productMediaGallery/saveData', this.dataItem)
        .then(response => {
          if (response && response.status) {
            this.$store.commit('productMediaGallery/RESET_DATA_ITEM')
            this.getDataList()
          }
        })
    },
    showToast(params) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: params.title,
          icon: params.icon,
          text: params.text,
          variant: params.variant,
        },
      }, {
        position: 'bottom-center',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.file-upload-container {
  width: 100%;
  position: relative;
}

// Progress Bar Overlay
.progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(2px);
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0.375rem;
}

.progress-bar-container {
  width: 80%;
  height: 8px;
  background-color: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #007bff, #0056b3);
  border-radius: 4px;
  transition: width 0.3s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-image: linear-gradient(
            -45deg,
            rgba(255, 255, 255, 0.2) 25%,
            transparent 25%,
            transparent 50%,
            rgba(255, 255, 255, 0.2) 50%,
            rgba(255, 255, 255, 0.2) 75%,
            transparent 75%,
            transparent
    );
    background-size: 20px 20px;
    animation: progress-stripes 1s linear infinite;
  }
}

@keyframes progress-stripes {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 0;
  }
}

.progress-text {
  color: #495057;
  font-weight: 500;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
}

.file-drop-zone {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px dashed #dee2e6 !important;
  position: relative;

  &:hover {
    border-color: #007bff !important;
    background-color: #f8f9fa !important;
  }

  &.border-primary {
    border-color: #007bff !important;
    background-color: #e3f2fd !important;
    transform: scale(1.01);
  }

  &.has-file {
    .card-body {
      padding: 0;
    }
  }

  &.downloading {
    pointer-events: none;
    opacity: 0.7;
  }
}

.upload-placeholder {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.selected-file {
  .file-info {
    .file-name {
      margin-bottom: 0.5rem;
      word-break: break-word;
    }

    .file-meta {
      font-size: 0.875rem;
    }
  }
}

// Bootstrap override'ları
.card-header {
  background-color: #f8f9fa !important;
  border-bottom: 1px solid #dee2e6;
}
</style>
